/** @jsx jsx */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from 'react';
import { jsx } from '@emotion/core';
import moment from 'moment';
import { Redirect, useHistory } from 'react-router-dom';
import SidebarTemplate from 'components/templates/SidebarTemplate';
import BlockUI from 'components/molecules/BlockUi';
import FormContents from 'components/atoms/Form/FormContents';
import OrganizationFilteredTree from 'components/organismos/organization/OrganizationFilteredTree/OrganizationFilteredTree';
import StatusBadge from 'components/molecules/StatusBadge';
import useToastNotification from 'hooks/useToastNotification';
import useCurrentRoleScreen from 'hooks/useCurrentRoleScreen';
import {
  getPeriods, getSubmissions, remindSubmissions, updatePeriod,
} from 'api/shiftBoard';
import { PeriodData, SubmissionsData } from 'domain/attend/shiftBoard/types';
import { formatDateTime, shortDateLabel } from 'domain/attend/shiftBoard/time';
import PeriodForm from 'components/organismos/master/attend/ShiftBoard/PeriodForm';
import SubmissionTable from 'components/organismos/master/attend/ShiftBoard/SubmissionTable';
import { boardStyles } from 'components/organismos/master/attend/ShiftBoard/styles';
import BoardSelect from 'components/organismos/master/attend/ShiftBoard/BoardSelect';
import { HIDE_SUBMISSIONS } from 'domain/attend/shiftBoard/phase';
import { describeError } from './useShiftBoard';

/**
 * 提出状況・回収枠
 * ルート: /shiftBoard/submissions
 *
 * フェーズ 1 では出さない（9/17 決定）。実装は残し、入口だけ閉じている。
 * 戻すときは domain/attend/shiftBoard/phase.ts の HIDE_SUBMISSIONS を false にする。
 */
const SubmissionsPage: React.FC = () => {
  const history = useHistory();
  const { errorNotification, successNotification } = useToastNotification();
  // 編集はアクション単位（9/16 決定）。メニュー権限だけ画面の表示可否に使う
  useCurrentRoleScreen('/shiftBoard/submissions');
  const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';
  const [orgCode, setOrgCode] = useState<string>(sessionStorage.getItem('shiftBoard.orgCode') || sessionStorage.getItem('loginUser.orgCode') || '');
  const [periods, setPeriods] = useState<Array<PeriodData>>([]);
  const [periodId, setPeriodId] = useState<string>('');
  const [data, setData] = useState<SubmissionsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const loadPeriods = useCallback(async () => {
    // フェーズ 1 では開けないので、読み込みもしない
    if (HIDE_SUBMISSIONS || !orgCode) {
      return;
    }
    setLoading(true);
    try {
      const list = await getPeriods(orgCode);
      setPeriods(list);
      if (list.length > 0 && !list.some((p) => p.periodId === periodId)) {
        setPeriodId(list[0].periodId);
      }
      if (list.length === 0) {
        setPeriodId('');
      }
    } catch (e) {
      errorNotification(describeError(e).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCode]);

  // 回収枠が 1 つも無いときも呼ぶ。編集権限はこの応答で受け取るため
  const loadSubmissions = useCallback(async () => {
    if (HIDE_SUBMISSIONS || !orgCode) {
      return;
    }
    setLoading(true);
    try {
      setData(await getSubmissions(orgCode, periodId));
    } catch (e) {
      errorNotification(describeError(e).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCode, periodId]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);
  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const current = periods.filter((p) => p.periodId === periodId)[0] || null;
  // 編集ひとつで判定する（9/17 決定）。on なら回収枠・締切変更・締め切る・催促のすべてができる
  const editable = !!(data && data.editable);

  const remind = async (staffCodes: Array<string>) => {
    if (!current) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(staffCodes.length > 0 ? `${staffCodes.length} 人に催促メールを送ります。よろしいですか？` : '未提出者全員に催促メールを送ります。よろしいですか？')) {
      return;
    }
    try {
      const r = await remindSubmissions(orgCode, current.periodId, staffCodes);
      successNotification(`${r.reminded} 人に催促しました。`);
      await loadSubmissions();
    } catch (e) {
      errorNotification(describeError(e).message);
    }
  };

  const toggleStatus = async () => {
    if (!current) {
      return;
    }
    try {
      await updatePeriod(orgCode, { periodId: current.periodId, status: current.status === 'collecting' ? 'closed' : 'collecting' });
      successNotification(current.status === 'collecting' ? '回収を締め切りました。' : '回収を再開しました。');
      await loadPeriods();
      await loadSubmissions();
    } catch (e) {
      errorNotification(describeError(e).message);
    }
  };

  const extend = async () => {
    if (!current) {
      return;
    }
    // eslint-disable-next-line no-alert
    const d = window.prompt('新しい締切日（YYYY-MM-DD）', moment(current.deadline).format('YYYY-MM-DD'));
    if (!d) {
      return;
    }
    try {
      await updatePeriod(orgCode, { periodId: current.periodId, deadline: d });
      successNotification('締切を変更しました。');
      await loadPeriods();
      await loadSubmissions();
    } catch (e) {
      errorNotification(describeError(e).message);
    }
  };

  // フェーズ 1 では開けない。直接 URL を叩かれたらシフト表へ戻す
  if (HIDE_SUBMISSIONS) {
    return <Redirect to="/shiftBoard" />;
  }

  return (
    <SidebarTemplate pageTitle="提出状況・回収枠">
      <BlockUI blocking={loading}>
        <FormContents>
          <div css={boardStyles.toolbar} style={{ position: 'static', border: 'none', padding: '0 0 8px 0' }}>
            <div style={{ minWidth: '260px' }}>
              <OrganizationFilteredTree
                functionType={2}
                staffCode={loginStaffCode}
                orgLabel="組織"
                initOrgValue={orgCode}
                orgCallback={(args: any) => {
                  const code = Array.isArray(args) ? args[0] : args;
                  if (code && code !== orgCode) {
                    sessionStorage.setItem('shiftBoard.orgCode', String(code));
                    setOrgCode(String(code));
                  }
                }}
                addAllItem={false}
                showSelectOrg={true}
                isAlignItemsCenter={true}
              />
            </div>
            {/* ほかの画面と同じ共通の Select（10/7） */}
            <BoardSelect
              id="sub-period"
              label="回収枠"
              value={periodId}
              onChange={setPeriodId}
              options={periods.map((p) => ({
                value: p.periodId,
                label: `${p.name}（${shortDateLabel(p.dateFrom)}〜${shortDateLabel(p.dateTo)}）${p.status === 'collecting' ? '' : ' 締切済'}`,
              }))}
              placeholder={periods.length === 0 ? '回収枠がありません' : undefined}
              style={{ width: '320px' }}
            />
            <div css={boardStyles.toolbarGroup} style={{ marginLeft: 'auto' }}>
              {editable && <button type="button" css={boardStyles.iconButton} data-active="true" onClick={() => setFormOpen(true)}>回収枠を作る</button>}
              {editable && current && <button type="button" css={boardStyles.iconButton} onClick={extend}>締切を変更</button>}
              {editable && current && <button type="button" css={boardStyles.iconButton} onClick={toggleStatus}>{current.status === 'collecting' ? '締め切る' : '再開する'}</button>}
              <button type="button" css={boardStyles.iconButton} onClick={() => history.push(current ? `/shiftBoard/${orgCode}/${current.dateFrom}` : '/shiftBoard')}>シフト編成へ</button>
            </div>
          </div>
          {current && (
            <div css={boardStyles.formRow}>
              <StatusBadge tone={current.status === 'collecting' ? 'info' : 'muted'} text={current.status === 'collecting' ? '回収中' : '締切済'} />
              <span>{`${current.name}: ${shortDateLabel(current.dateFrom)} 〜 ${shortDateLabel(current.dateTo)}`}</span>
              <span css={boardStyles.subText}>{`締切 ${formatDateTime(current.deadline)}`}</span>
              <span css={boardStyles.subText}>{`形式: ${current.format === 'pattern' ? 'パターン' : current.format === 'time' ? '時間' : '○×'}`}</span>
              {current.confirmedAt && <StatusBadge tone="ok" text={`確定 ${formatDateTime(current.confirmedAt)}`} />}
            </div>
          )}
          {data && <SubmissionTable data={data} editable={editable && !!current && current.status === 'collecting'} onRemind={remind} />}
          {!current && !loading && <div css={boardStyles.subText}>回収枠を作ると、提出状況と催促ができます。スタッフは従来どおりマイシフトから希望を提出します。</div>}
        </FormContents>
      </BlockUI>
      <PeriodForm
        open={formOpen}
        orgCode={orgCode}
        onClose={() => setFormOpen(false)}
        onCreated={(p) => {
          setFormOpen(false);
          successNotification('回収枠を作成しました。');
          setPeriodId(p.periodId);
          loadPeriods();
        }}
      />
    </SidebarTemplate>
  );
};

export default SubmissionsPage;
